import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {toastr} from "react-redux-toastr";
import { api } from "../../../services/api/api.service";
import { IClientState, IClient } from "./ClientsCRUD.types";
import { API_CLIENTS } from "../../../services/api/api.urls";
import { TryCatch } from "../../../services/TryCatch";
import { COMPANY_ABBR } from "../../../common/constants/company";

export interface IGetClientResponse {
    totalItems: number,
    totalPages: number,
    pageNum: number
    items:IClient[]
}

const initialState: IClientState = {
    allClients: [] as IClient[],
    client: {} as IClient,
    loader: true,
    createMode: false,
    editMode: false,
    totalItems: 0,
    totalPages: 0,
    pageNum: 1
}

export const getClients = createAsyncThunk<IGetClientResponse,string>(
    'clients/get',
    async (query, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_CLIENTS}${query ? `?${query}` : ""}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to fetch clients.')
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const getSingleClient = createAsyncThunk<IClient, number>(
    'clients/getSingle',
    async (id, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_CLIENTS}/${id}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to fetch a single client.')
            return rejectWithValue(result.error);
        }
        return result.data;
    }
);

export const createClient = createAsyncThunk<IClient, IClient>(
    'clients/post',
    async (user, {rejectWithValue}) => {
        let status = 0;
        const result = await TryCatch(
            () => api.post(API_CLIENTS, user).then(res => { status = res.status; return res.data; })
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to create a client.')
            return rejectWithValue(result.error)
        }
        toastr.success(COMPANY_ABBR, "Client has been created");
        return result.data;
    }
)

export const editClient = createAsyncThunk<IClient, IClient>(
    'client/edit',
    async (payload, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.put(`${API_CLIENTS}`, payload).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to edit a client.')
            return rejectWithValue(result.error)
        }
        toastr.success(COMPANY_ABBR, "Client has been edited");
        return result.data;
    }
)

export const deleteClient = createAsyncThunk<IClient, number>(
    'clients/delete',
    async (id, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.delete(`${API_CLIENTS}/${id}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to delete a client.')
            return rejectWithValue(result.error)
        }
        toastr.success(COMPANY_ABBR, "Client has been deleted");
        return result.data;
    }
)

export const ClientsCRUD = createSlice({name: 'ClientsCRUD',
    reducers: {
        createModeHandler: (state, action: PayloadAction<boolean>) => {
            state.createMode = action.payload;
        },
        editModeHandler: (state, action: PayloadAction<boolean>) => {
            state.editMode = action.payload;
        },
        clearSingleClientState: (state) => {
            state.client = initialState.client;
        },
    },
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(getClients.fulfilled, (state: IClientState, action) => {
                state.loader = false;
                state.allClients = action.payload.items;
            })
            .addCase(getClients.rejected, (state: IClientState) => {
                state.loader = false;
            })
            .addCase(getSingleClient.fulfilled, (state: IClientState, action) => {
                state.editMode = true;
                state.client = action.payload;
            })
            .addCase(createClient.pending, (state: IClientState) => {
                state.createMode = false;
                state.loader = true;
            })
            .addCase(createClient.fulfilled, (state: IClientState, action) => {
                state.allClients = state.allClients.concat(action.payload);
                state.loader = false;
            })
            .addCase(createClient.rejected, (state: IClientState) => {
                state.loader = false
            })
            .addCase(editClient.pending, (state: IClientState) => {
                state.editMode = false;
                state.loader = true;
            })
            .addCase(editClient.fulfilled, (state: IClientState, action) => {
                state.loader = false;
                //state.allClients = state.allClients.map((client) => {
                //    if (client.id === action.payload.id) {
                //        client = action.payload
                //    }
                //    return client
                //})

            })
            .addCase(editClient.rejected, (state: IClientState) => {
                state.loader = false;
            })
            .addCase(deleteClient.pending, (state: IClientState) => {
                state.loader = true;
            })
            .addCase(deleteClient.fulfilled, (state: IClientState, action) => {
                //state.allClients = state.allClients.filter((client) => {
                //    return client.id !== action.meta.arg
                //})
                state.loader = false;
            })
            .addCase(deleteClient.rejected, (state: IClientState) => {
                state.loader = false;
            })
    },
});
export const {
    createModeHandler,
    editModeHandler,
    clearSingleClientState
} = ClientsCRUD.actions

export default ClientsCRUD.reducer;
